import { useState } from "react";

/**
 * Step Manager. React Hook.
 *
 * Provides reasonably generic, but rolled up stepper data management
 * functionality that is fairly common.
 *
 * If this does not meet your needs, do not try to extend this, and instead make
 * a customized variant using the mui primitives.
 *
 * @see https://mui.com/material-ui/react-stepper/
 *
 * 💡 Where are all the useCallbacks and useMemos? This hook is so basic that it
 * is unlikely that either optimization will help, and will only contribute
 * complexity.
 *
 * @param steps a list of steps; steps are uniquely identified by their position
 * in this array 🍎 if your steps are conditional, you should consider a
 * different approach
 * @returns react hook object containing data, helpers
 */
export function useStepManager(steps: string[]) {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{
    [k: number]: boolean;
  }>({});

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    handleNext();
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };

  const handleSetStep = (step: number) => { 
    
    const newCompleted:{
      [k: number]: boolean;
    } = {};
    for (let index = 0; index < step; index++) {
      newCompleted[index] = true;
    }
    setCompleted(newCompleted);
    setActiveStep(step);
  };

  return {
    activeStep,
    completed,
    completedSteps,
    isLastStep,
    allStepsCompleted,
    handleNext,
    handleBack,
    handleComplete,
    handleReset,
    handleSetStep,
  };
}
